import { Button, EmptyState, SearchField, Skeleton, cn } from '@h2hspark/ui';
import { Bookmark, Users } from 'lucide-react';
import * as React from 'react';

import { AttendeeCard, PrivateAttendeesRow } from '../attendee-preview/attendee-card';
import { SaveToMeetButton } from '../attendee-preview/save-to-meet-panel';
import type { AttendeeRoster } from '../attendee-preview/types';
import { ConnectButton } from '../connections/connect-action-panel';

import {
  ATTENDEES_CLOSED_TITLE,
  ATTENDEES_EMPTY_DESCRIPTION,
  ATTENDEES_EMPTY_TITLE,
  ATTENDEES_NO_MATCH_DESCRIPTION,
  ATTENDEES_NO_MATCH_TITLE,
  ATTENDEES_REGION_ID,
  ATTENDEES_SEARCH_CLEAR_LABEL,
  ATTENDEES_SEARCH_LABEL,
  ATTENDEES_SEARCH_PLACEHOLDER,
  MEET_LIST_EMPTY_ACTION,
  MEET_LIST_EMPTY_DESCRIPTION,
  MEET_LIST_EMPTY_TITLE,
  MEET_ONLY_LABEL,
  attendeesHeading,
  attendeesMatching,
  attendeesSubhead,
  meetOnlyControlName,
} from './constants';

/*
  *Everyone attending* and its meet-list toggle, drawn on two pages: the event's Matches page
  (`MEM-025`, under the match grid) and the full attendee list that "See all" under Who's coming
  opens (`MEM-011` AC-4, as Brad redrew it on 2026-09-25 — TW 44189308). One component, so the two
  rooms cannot drift apart.
*/

/** §3.6's meet-list toggle (`meetOnlyStyle`): outline-brand off, filled `--brand-primary` on. */
export function MeetOnlyToggle({
  count,
  on,
  onToggle,
}: {
  readonly count: number;
  readonly on: boolean;
  readonly onToggle: () => void;
}): React.ReactElement {
  return (
    <button
      type="button"
      aria-pressed={on}
      aria-label={meetOnlyControlName({ count, on })}
      aria-controls={ATTENDEES_REGION_ID}
      onClick={onToggle}
      className={cn(
        'inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-pill border-[1.5px] border-primary px-[18px] py-3 font-brand text-sm font-bold',
        on ? 'bg-primary text-primary-foreground' : 'bg-card text-primary',
      )}
    >
      <Bookmark className="h-4 w-4" aria-hidden />
      {MEET_ONLY_LABEL}
      <span
        className={cn(
          'inline-flex h-5 min-w-5 items-center justify-center rounded-pill px-1.5 text-[11px] font-black',
          on ? 'bg-white/25 text-white' : 'bg-primary text-primary-foreground',
        )}
      >
        {count}
      </span>
    </button>
  );
}

/**
 * *Everyone attending* (§2.4, item 4) — the room, with its own search and its own grid.
 *
 * The roster is the server's answer, closed or open: an organizer who switched the preview off, or
 * a preview window that has not opened, comes back `closed` with nobody in it and its own sentence
 * (`MEM-011` AC-7). A registrant sees the list; that gate is the API's and this screen draws what
 * it is sent.
 */
export function EveryoneAttending({
  eventId,
  roster,
  pending,
  savedRefs,
  savedTotal,
  meetOnly,
  query,
  onQuery,
  onBrowseEveryone,
  headingLevel = 'h3',
  toggle = null,
}: {
  readonly eventId: string;
  readonly roster: AttendeeRoster | undefined;
  readonly pending: boolean;
  readonly savedRefs: ReadonlySet<string>;
  readonly savedTotal: number;
  readonly meetOnly: boolean;
  readonly query: string;
  readonly onQuery: (next: string) => void;
  readonly onBrowseEveryone: () => void;
  /** `h3` under the Matches page's own heading; `h1` where the room is the page (the roster). */
  readonly headingLevel?: 'h1' | 'h3';
  /** The meet-list toggle, where the page has no heading row of its own to put it in. */
  readonly toggle?: React.ReactNode;
}): React.ReactElement {
  const Heading = headingLevel;
  const attendees = roster?.attendees ?? [];
  const shown = attendeesMatching(attendees, { query, meetOnly, savedRefs });
  const profilePath = (attendeeRef: string): string =>
    `/app/events/${encodeURIComponent(eventId)}/attendees/${encodeURIComponent(attendeeRef)}`;

  return (
    <section id={ATTENDEES_REGION_ID} aria-labelledby="everyone-attending-heading" className="pt-4">
      <div className="mb-5 flex flex-wrap items-end justify-between gap-5">
        <div className="min-w-0">
          <Heading
            id="everyone-attending-heading"
            className="mb-1.5 text-xl tracking-tight md:text-2xl"
          >
            {attendeesHeading(meetOnly)}
          </Heading>
          <p className="max-w-[600px] text-sm text-muted-foreground md:text-md">
            {attendeesSubhead(meetOnly)}
          </p>
        </div>
        <div className="flex w-full flex-wrap items-center gap-3 sm:w-auto">
          {toggle}
          {/* §2.4's 280px search pill. Not drawn over a closed roster: there is nothing to search. */}
          {roster !== undefined && roster.closed === null && attendees.length > 0 ? (
            <SearchField
              size="member"
              value={query}
              onChange={onQuery}
              label={ATTENDEES_SEARCH_LABEL}
              clearLabel={ATTENDEES_SEARCH_CLEAR_LABEL}
              placeholder={ATTENDEES_SEARCH_PLACEHOLDER}
              className="w-full sm:w-[280px]"
            />
          ) : null}
        </div>
      </div>

      {pending ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Skeleton className="h-[86px] w-full rounded-lg" />
          <Skeleton className="h-[86px] w-full rounded-lg" />
        </div>
      ) : roster === undefined ? null : roster.closed !== null ? (
        // A roster that has not opened yet is not an empty one — TW 44159943. It says when it
        // opens in the line underneath, and "Nobody on the list yet" contradicted that.
        <EmptyState
          icon={<Users className="h-6 w-6" aria-hidden />}
          title={ATTENDEES_CLOSED_TITLE}
          description={roster.closed.notice}
        />
      ) : meetOnly && savedTotal === 0 ? (
        // The prototype's `meetEmptyInline`: the toggle is on and nothing at all is saved.
        <EmptyState
          icon={<Bookmark className="h-6 w-6" aria-hidden />}
          title={MEET_LIST_EMPTY_TITLE}
          description={MEET_LIST_EMPTY_DESCRIPTION}
          action={
            /* Was a hand-drawn copy of the brand's Secondary pill, written here because the shared
               button had no brand variant to reach for. It does now. */
            <Button type="button" variant="brand-outline" size="lg" onClick={onBrowseEveryone}>
              <Users className="h-[17px] w-[17px]" aria-hidden />
              {MEET_LIST_EMPTY_ACTION}
            </Button>
          }
        />
      ) : attendees.length === 0 && roster.privateAttendees.count === 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" aria-hidden />}
          title={ATTENDEES_EMPTY_TITLE}
          description={ATTENDEES_EMPTY_DESCRIPTION}
        />
      ) : shown.length === 0 && query.trim().length > 0 ? (
        <EmptyState
          icon={<Users className="h-6 w-6" aria-hidden />}
          title={ATTENDEES_NO_MATCH_TITLE}
          description={ATTENDEES_NO_MATCH_DESCRIPTION}
        />
      ) : (
        <ul className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {shown.map((card) => (
            <li key={card.attendeeRef} className="min-w-0">
              <AttendeeCard
                card={card}
                to={profilePath(card.attendeeRef)}
                actions={
                  <>
                    <SaveToMeetButton
                      eventId={eventId}
                      attendeeRef={card.attendeeRef}
                      saved={savedRefs.has(card.attendeeRef)}
                      variant="attendee"
                    />
                    <ConnectButton
                      eventId={eventId}
                      attendeeRef={card.attendeeRef}
                      person={card}
                      /* Blank, as the attendee profile's composer is (`MEM-032` AC-4 pre-fills
                         from a **match** and only from one). The prototype writes "we're both
                         going to this event" here; putting words in a member's mouth about a
                         person they have only seen on a list is a sentence they did not choose. */
                      relationship={card.relationship}
                      connectionRef={card.connectionRef}
                      prefilledMessage={null}
                      variant="attendee"
                    />
                  </>
                }
              />
            </li>
          ))}
          {/* §2.4: the private row spans both columns, and is a sibling of the entries rather than
              one of them. Hidden under the meet-list filter, as the prototype's `notMeetOnly`
              does — it is a statement about the room, not about the member's own list. */}
          {meetOnly ? null : (
            <PrivateAttendeesRow
              privateAttendees={roster.privateAttendees}
              className="md:col-span-2"
            />
          )}
        </ul>
      )}
    </section>
  );
}
